// src/features/orders/components/OrderRow.tsx
import clsx from 'clsx';
import type { Order } from '../types/orders.types';
import { OrderStatusBadge } from './OrderStatusBadge';
import '../styles/orders.css';

interface OrderRowProps {
  order: Order;
  onSelect?: ((order: Order) => void) | undefined;
  isSelected?: boolean;
}

/**
 * Compact premium row for the dense SaaS admin Orders list.
 * Hierarchy: primary = id + service name, secondary = customer + date,
 * emphasis = amount, trailing = semantic status badge.
 * Selecting a row opens the Order Workspace for that order.
 */
export function OrderRow({ order, onSelect, isSelected = false }: OrderRowProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect?.(order)}
      aria-pressed={isSelected}
      aria-label={`باز کردن فضای کاری سفارش ${order.customerName}`}
      className={clsx(
        'vania-order-row flex w-full items-center gap-3 rounded-md border px-3 py-2 text-right',
        'border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))] bg-transparent',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="shrink-0 text-[11px] font-medium text-[var(--vania-colors-text-secondary,#a3aab8)]">
            #{order.id}
          </span>
          <span className="truncate text-sm font-medium text-[var(--vania-colors-text-primary,#f5f6fa)]">
            {order.serviceName}
          </span>
        </div>

        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-[var(--vania-colors-text-secondary,#a3aab8)]">
          <span className="truncate">{order.customerName}</span>
          <span className="shrink-0">·</span>
          <span className="shrink-0">{order.date}</span>
        </div>
      </div>

      <span className="vania-order-amount shrink-0 text-sm font-semibold text-[var(--vania-colors-text-primary,#f5f6fa)]">
        {order.amount}
      </span>

      <OrderStatusBadge status={order.status} />
    </button>
  );
}
